import { post } from "../../helper/Api_helper";

import {
  RESUME_PARSER_BASE_URL,
  PARSED_RESUME_URL,
  PROCESS_UPLOAD_FILE,
  VYLABS_BASE_URL,
  CREATE_RUN_SET_ID,
  GET_UNPARSED_RESUMES_COUNT,
  WEB_SOCKET_URL,
} from "../../helper/url_helper";
import envConfig from "../../helper/env_config";
export async function parseResumeHeadless(file, options = {}) {
  const signal = options.signal;
  const staticDiceVendorId =envConfig.vendorId;
  try {
    if (signal?.aborted) {
      return { error: 'Operation aborted before start' };
    }

    const token = localStorage.getItem("token");
    let ws;
    let pingInterval;
    let pingTimeout;

    const openWebSocket = () => {
      try {
        const wsProtocol = window.location.protocol === "https:" ? "wss://" : "wss://";
        const baseHost = String(WEB_SOCKET_URL)
          .replace(/^wss?:\/\//, "")
          .replace(/^https?:\/\//, "")
          .replace(/^\/+/, "");
        const wsUrl = `${wsProtocol}${baseHost}/ws?token=${encodeURIComponent(
          "Bearer " + token
        )}`;
        console.log("WebSocket connection established");

        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.warn("Sending initial ping");
          const sendPing = () => {
            if (ws && ws.readyState === WebSocket.OPEN && !signal?.aborted) {
              ws.send("ping");
              console.warn("Sending ping");
              if (pingTimeout) clearTimeout(pingTimeout);
              pingTimeout = setTimeout(() => {
                console.log("No pong received within timeout, reconnecting...");
                try { ws.close(1000, "Ping timeout"); } catch (e) {}
              }, 10000);
            }
          };
          sendPing();
          pingInterval = setInterval(() => {
            if (!signal?.aborted) {
              sendPing();
            } else {
              clearInterval(pingInterval);
            }
          }, 30000);
        };

        ws.onmessage = (evt) => {
          if (evt.data === "pong") {
            console.warn("Pong received");
            if (pingTimeout) { clearTimeout(pingTimeout); pingTimeout = null; }
          }
        };

        ws.onclose = () => {
          console.log("WebSocket connection closed");
          if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
          if (pingTimeout) { clearTimeout(pingTimeout); pingTimeout = null; }
        };

        ws.onerror = (e) => {
          console.error("WebSocket error:", e);
        };

      } catch (e) {
        console.warn("[HeadlessParser] Failed to open WS", e);
      }
    };

    const closeWebSocket = () => {
      try { if (ws) ws.close(); } catch (e) {}
      if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
      if (pingTimeout) { clearTimeout(pingTimeout); pingTimeout = null; }
    };

    openWebSocket();

    if (signal) {
      signal.addEventListener('abort', () => {
        closeWebSocket();
      });
    }

    if (signal?.aborted) return { error: 'Operation aborted' };

    const runSetId = await post(VYLABS_BASE_URL, CREATE_RUN_SET_ID, {
      folderName: file?.name || "dice-import",
      totalFiles: 1,
    });

    if (!runSetId) {
      closeWebSocket();
      return { error: "Failed to create run set" };
    }

    if (signal?.aborted) {
      closeWebSocket();
      return { error: 'Operation aborted' };
    }

    const requestBody = JSON.stringify({ FileNames: [file.name] });
    const presignedResp = await fetch(
      RESUME_PARSER_BASE_URL + PARSED_RESUME_URL,
      {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: requestBody,
      }
    );

    if (!presignedResp.ok) {
      closeWebSocket();
      return { error: "Failed to get presigned URL" };
    }

    const presignedData = await presignedResp.json();
    const presignedUrl = presignedData?.PresignedUploadURLs?.[0];

    if (!presignedUrl) {
      closeWebSocket();
      return { error: "No presigned URL returned" };
    }

    const uploadResp = await fetch(presignedUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": "application/octet-stream" },
      mode: "cors",
    });

    if (!uploadResp.ok) {
      closeWebSocket();
      return { error: "Failed to upload to presigned URL" };
    }

    const processBody = {
      fileNames: [file.name],
      runSetID: runSetId,
      vendorId: staticDiceVendorId,
    };

    const processResp = await fetch(RESUME_PARSER_BASE_URL + PROCESS_UPLOAD_FILE, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(processBody),
    });

    if (!processResp.ok) {
      closeWebSocket();
      return { error: "Failed to start processing" };
    }

    await new Promise((r) => setTimeout(r, 20000));

    let lastFailReason = null;
    let status;
    const MAX_ATTEMPTS = 3;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (signal?.aborted) {
        closeWebSocket();
        return { error: 'Operation aborted during polling' };
      }

      try {
        status = await post(VYLABS_BASE_URL, GET_UNPARSED_RESUMES_COUNT, {
          runSetID: runSetId,
        });
        const completed = status?.completed_files || [];
        const found = completed.find((f) => f?.name === file.name && f?.candidate_id);
        if (found) {
          break;
        }
      } catch (e) {
        console.warn(`Status check attempt ${attempt + 1} failed:`, e);
      }
      if (attempt < (MAX_ATTEMPTS - 1)) {
        await new Promise((r) => setTimeout(r, 20000));
      }
    }

    closeWebSocket();

    const completed = status?.completed_files || [];
    const incomplete = status?.incomplete_files || [];
    const failed = status?.failed_files || [];

    const completedFile = completed.find(f => f?.name === file.name);
    if (completedFile?.candidate_id) {
      return {
        candidateId: completedFile.candidate_id,
        fileID: completedFile.id || completedFile.file_id || completedFile.fileID
      };
    }

    const incompleteFile = incomplete.find(f => f?.name === file.name);
    if (incompleteFile?.id) {
      return { candidateId: null, fileID: incompleteFile.id, status: "INCOMPLETE" };
    }

    const failedFile = failed.find(f => f?.name === file.name);
    if (failedFile?.id) {
      lastFailReason = failedFile.message || "parse_failed";
      return { candidateId: null, fileID: failedFile.id, status: "FAILED" };
    }
    
    return {
      error: lastFailReason || "Parsing timeout",
      isDefinitiveFailure: true,
    };

  } catch (err) {
    if (err.name === 'AbortError' || signal?.aborted) {
      return { error: 'Operation aborted' };
    }
    return {
      error: err?.message || "Unknown headless parse error",
      isDefinitiveFailure: true
    };
  }
}
